import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  type Analyse,
  type Befund,
  type LonLat,
  type Stelle,
  type Veranstaltung,
  type ZugParameter,
} from '../api/client';
import { Schritte } from '../components/Schritte';
import { Annahme, StatusPill, fehlerText } from '../components/ui';
import { MASSNAHME_TEXT, abschnitt, punktBei, uhrzeit, zugPosition } from '../format';
import { MapView, type Werkzeug } from '../map/MapView';
import { tempo, wegeklasse } from '../map/osmText';
import type { KartenPunkt } from '../map/punkteLayer';
import { link } from '../router';
import { befundBestaetigen, routeAendern } from '../aktionen';
import { useUndo } from '../undo';

type Zustand = 'laedt' | 'bereit' | 'speichert' | 'prueft' | { fehler: string };

/** Skizze 3: Karte mit Route, Stellen mit Ampel und Begründung, Zeitregler „Wo ist der Zug um …“. */
export function Planung({ id, darf }: { id: string; darf: boolean }) {
  const [v, setV] = useState<Veranstaltung | null>(null);
  const [route, setRoute] = useState<LonLat[] | null>(null);
  const [laenge, setLaenge] = useState<number | null>(null);
  const [parameter, setParameter] = useState<ZugParameter>({
    geh_kmh: null,
    personen_pro_reihe: null,
    reihenabstand_m: null,
  });
  const [analyse, setAnalyse] = useState<Analyse | null>(null);
  const [ausgewaehlt, setAusgewaehlt] = useState<string | null>(null);
  const [werkzeug, setWerkzeug] = useState<Werkzeug>('auswaehlen');
  const [zustand, setZustand] = useState<Zustand>('laedt');
  const [zeit, setZeit] = useState<number | null>(null);
  const lauf = useRef(0);
  const pv = v?.planversion_id ?? null;
  const undo = useUndo();
  const stand = undo.stand;
  // Letzter gespeicherter Stand der Route – Grundlage für „Rückgängig“
  const gespeichert = useRef<{ coords: LonLat[]; parameter: ZugParameter } | null>(null);

  const pruefen = useCallback(async (planversion: string, nr: number) => {
    setZustand('prueft');
    try {
      const r = await api.analysieren(planversion);
      if (nr !== lauf.current) return;
      setAnalyse(r.analyse);
      setZustand('bereit');
    } catch (e) {
      if (nr === lauf.current) setZustand({ fehler: fehlerText(e) });
    }
  }, []);

  useEffect(() => {
    let aktiv = true;
    void (async () => {
      try {
        const { veranstaltung } = await api.veranstaltung(id);
        if (!aktiv) return;
        setV(veranstaltung);
        const planversion = veranstaltung.planversion_id;
        if (!planversion) return setZustand({ fehler: 'Keine Planversion vorhanden.' });
        const [r, a] = await Promise.all([api.route(planversion), api.analyse(planversion)]);
        if (!aktiv) return;
        if (r.route) {
          const p = r.route.properties.parameter ?? {
            geh_kmh: null,
            personen_pro_reihe: null,
            reihenabstand_m: null,
          };
          setRoute(r.route.geometry.coordinates);
          setLaenge(r.route.properties.laenge_m);
          setParameter(p);
          gespeichert.current = { coords: r.route.geometry.coordinates, parameter: p };
        }
        // Nach Rückgängig/Wiederholen neu prüfen, sonst die gespeicherte Analyse zeigen
        if (a.analyse && stand === 0) {
          setAnalyse(a.analyse);
          setZustand('bereit');
        } else if (r.route) {
          await pruefen(planversion, ++lauf.current);
        } else {
          setZustand('bereit');
        }
      } catch (e) {
        if (aktiv) setZustand({ fehler: fehlerText(e) });
      }
    })();
    return () => {
      aktiv = false;
    };
  }, [id, pruefen, stand]);

  const speichern = useCallback(
    async (coords: LonLat[], p: ZugParameter, text?: string) => {
      if (!pv) return;
      const nr = ++lauf.current;
      setRoute(coords);
      setZustand('speichert');
      try {
        const neu = { coords, parameter: p };
        await undo.tue(routeAendern(pv, gespeichert.current, neu, text));
        gespeichert.current = neu;
        const r = await api.route(pv);
        if (nr !== lauf.current) return;
        setLaenge(r.route?.properties.laenge_m ?? null);
        await pruefen(pv, nr);
      } catch (e) {
        if (nr === lauf.current) setZustand({ fehler: fehlerText(e) });
      }
    },
    [pv, pruefen, undo],
  );

  async function bestaetigen(b: Befund, aufheben = false) {
    if (!pv) return;
    try {
      await undo.tue(befundBestaetigen(pv, b, aufheben));
      await pruefen(pv, ++lauf.current);
    } catch (e) {
      setZustand({ fehler: fehlerText(e) });
    }
  }

  const punkte = useMemo<KartenPunkt[]>(() => {
    if (!analyse) return [];
    return [
      ...stellenNummern(analyse.stellen).map(([s, nr]) => ({
        id: s.id,
        position: s.position.coordinates,
        status: s.status,
        label: s.kennung && s.herkunft !== 'erkannt' ? s.kennung : String(nr),
        vorgabe: s.herkunft === 'angeordnet',
      })),
      ...analyse.hinweise.map((h) => ({
        id: h.id,
        position: h.position.coordinates,
        status: 'grau' as const,
        label: '',
        klein: true,
      })),
    ];
  }, [analyse]);

  const popup = useCallback(
    (pid: string) => {
      const s = analyse?.stellen.find((x) => x.id === pid);
      if (s) return stellePopup(s);
      const h = analyse?.hinweise.find((x) => x.id === pid);
      if (!h) return null;
      const div = document.createElement('div');
      div.className = 'popup';
      div.textContent = `${h.bezeichnung} – ${h.begruendung}`;
      return div;
    },
    [analyse],
  );

  // Zeitregler
  const start = analyse ? Date.parse(analyse.zug.start) : null;
  const ende = analyse ? Date.parse(analyse.zug.ende) : null;
  const zugLage = useMemo(() => {
    if (!analyse || !route || zeit === null || start === null) return null;
    const p = zugPosition(
      zeit,
      start,
      analyse.zug.geh_kmh,
      analyse.zug.laenge_m,
      analyse.zug.route_laenge_m,
    );
    if (!p) return null;
    return {
      linie: abschnitt(route, p.ende, p.spitze),
      spitze: punktBei(route, p.spitze),
      ende: punktBei(route, p.ende),
    };
  }, [analyse, route, zeit, start]);

  const zaehler = analyse?.zaehler;

  return (
    <div className="planung">
      <Schritte id={id} aktiv="planung" titel={v?.titel} />
      <div className="planung__flaeche">
        <div className="werkzeuge" role="toolbar" aria-label="Werkzeuge">
          <WerkzeugKnopf
            aktiv={werkzeug === 'auswaehlen'}
            onClick={() => setWerkzeug('auswaehlen')}
            symbol="↖"
            text="Auswählen"
          />
          <WerkzeugKnopf
            aktiv={werkzeug === 'bearbeiten'}
            onClick={() => setWerkzeug('bearbeiten')}
            symbol="✎"
            text="Route ändern"
            disabled={!pv || !darf || !route}
          />
          <WerkzeugKnopf
            aktiv={werkzeug === 'route'}
            onClick={() => setWerkzeug('route')}
            symbol="〰"
            text={route ? 'Neu zeichnen' : 'Route'}
            disabled={!pv || !darf}
          />
          <a
            className="werkzeug"
            href={link('v', id, 'kraefte')}
            title="Posten in „Kräfte zuordnen“ anlegen"
          >
            <span className="werkzeug__symbol" aria-hidden="true">
              ⚑
            </span>
            <span>Posten</span>
          </a>
          {['Fahrzeug', 'Fläche', 'Notiz'].map((t) => (
            <WerkzeugKnopf
              key={t}
              aktiv={false}
              symbol="·"
              text={t}
              disabled
              title="Folgt in einem späteren Schritt"
            />
          ))}
        </div>

        <div className="planung__karte">
          <MapView
            route={route}
            // Nur in den Routen-Werkzeugen bearbeitbar – sonst schützt das vor versehentlichen Änderungen (Autosave).
            routeBearbeitbar={darf && werkzeug !== 'auswaehlen'}
            punkte={punkte}
            ausgewaehltId={ausgewaehlt}
            werkzeug={werkzeug}
            zug={zugLage}
            onRouteAenderung={(c) => void speichern(c, parameter)}
            onRouteGezeichnet={() => setWerkzeug('auswaehlen')}
            onAuswahl={setAusgewaehlt}
            popupInhalt={popup}
          />
          {analyse && start !== null && ende !== null && (
            <div className="zeitregler">
              <label htmlFor="zeitregler">
                Wo ist der Zug um{' '}
                <strong>{zeit === null ? '…' : uhrzeit(new Date(zeit).toISOString())}</strong>?
              </label>
              <input
                id="zeitregler"
                type="range"
                min={start - 15 * 60_000}
                max={ende + 15 * 60_000}
                step={60_000}
                value={zeit ?? start}
                onChange={(e) => setZeit(Number(e.target.value))}
              />
              <span className="gedaempft">
                Start {uhrzeit(analyse.zug.start)} · Zugende am Ziel ca. {uhrzeit(analyse.zug.ende)}{' '}
                ·{' '}
                {zeit !== null && !zugLage
                  ? zeit < start
                    ? 'Zug noch nicht gestartet'
                    : 'Zug angekommen'
                  : `Zuglänge ca. ${Math.round(analyse.zug.laenge_m)} m`}
              </span>
            </div>
          )}
        </div>

        <aside className="seitenleiste" aria-label="Stellen">
          <div className="seitenleiste__kopf">
            <h2>{v?.titel ?? 'Planung'}</h2>
            <StatusZeile zustand={zustand} laenge={laenge} werkzeug={werkzeug} />
            {zaehler && (
              <p className="zaehler">
                {analyse.vollstaendig ? (
                  <StatusPill status="gruen" text="Plan vollständig" />
                ) : (
                  <>
                    <StatusPill status="rot" text={`${zaehler.rot} rot`} />{' '}
                    <StatusPill status="gelb" text={`${zaehler.gelb_offen} gelb offen`} />
                  </>
                )}
              </p>
            )}
            {darf && route && (
              <button
                type="button"
                className="knopf"
                onClick={() => pv && void pruefen(pv, ++lauf.current)}
              >
                Erneut prüfen
              </button>
            )}
          </div>

          {analyse && (
            <>
              {analyse.befunde_global.map((b) => (
                <BefundZeile key={b.schluessel} b={b} darf={darf} onBestaetigen={bestaetigen} />
              ))}
              <ol className="stellen__liste" aria-label="Stellen nach Status">
                {stellenNummern(analyse.stellen).map(([s, nr]) => (
                  <li key={s.id}>
                    <StellenEintrag
                      s={s}
                      nr={nr}
                      aktiv={s.id === ausgewaehlt}
                      darf={darf}
                      onAuswahl={() => setAusgewaehlt(s.id)}
                      onBestaetigen={bestaetigen}
                    />
                  </li>
                ))}
              </ol>
              {analyse.hinweise.length > 0 && (
                <details className="stellen__hinweise">
                  <summary>
                    Hinweise: Fuß-/Radwege und Zufahrten ({analyse.hinweise.length})
                  </summary>
                  <ul className="einfache-liste">
                    {analyse.hinweise.map((h) => (
                      <li key={h.id}>
                        <button type="button" className="link" onClick={() => setAusgewaehlt(h.id)}>
                          {h.bezeichnung}
                        </button>{' '}
                        – Route-Meter {Math.round(h.station_m)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <section>
                <h3>Auflagen</h3>
                {analyse.auflagen.length === 0 && (
                  <p className="gedaempft">Keine Auflagen erfasst.</p>
                )}
                <ul className="einfache-liste">
                  {analyse.auflagen.map((a) => (
                    <li key={a.id}>
                      <StatusPill status={a.status} /> {a.text}{' '}
                      {a.fundstelle && <span className="gedaempft">({a.fundstelle})</span>}
                      <div className="gedaempft">{a.befund.begruendung}</div>
                    </li>
                  ))}
                </ul>
                <a href={link('v', id, 'vorgaben')}>Auflagen abhaken unter „Vorgaben“</a>
              </section>
              <ZugAnnahmen
                analyse={analyse}
                parameter={parameter}
                darf={darf && !!route}
                onSpeichern={(p) => {
                  setParameter(p);
                  if (route) void speichern(route, p, 'Zugmodell geändert');
                }}
              />
            </>
          )}
          <p className="seitenleiste__fuss">
            Die App erkennt Kreuzungen und gleicht sie mit der Anordnung ab. Sperrungen legt sie nie
            selbst fest – nicht angeordnete Kreuzungen sind rot markiert und mit der Behörde zu
            klären.
          </p>
        </aside>
      </div>
    </div>
  );
}

/** Nummern 1…n in Reihenfolge der Route (für Karte, Liste und Druck gleich). */
export function stellenNummern(stellen: Stelle[]): [Stelle, number][] {
  const nachRoute = [...stellen].sort((a, b) => (a.station_m ?? 1e9) - (b.station_m ?? 1e9));
  const nr = new Map(nachRoute.map((s, i) => [s.id, i + 1]));
  return stellen.map((s) => [s, nr.get(s.id) ?? 0]);
}

function StellenEintrag({
  s,
  nr,
  aktiv,
  darf,
  onAuswahl,
  onBestaetigen,
}: {
  s: Stelle;
  nr: number;
  aktiv: boolean;
  darf: boolean;
  onAuswahl: () => void;
  onBestaetigen: (b: Befund, aufheben?: boolean) => void;
}) {
  return (
    <div
      className={`stelle stelle--${s.status}${aktiv ? ' stelle--aktiv' : ''}`}
      ref={(el) => {
        if (aktiv) el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      }}
    >
      <button type="button" className="stelle__kopf" aria-pressed={aktiv} onClick={onAuswahl}>
        <span className="stelle__nr">{s.kennung && s.herkunft !== 'erkannt' ? s.kennung : nr}</span>
        <span className="stelle__text">
          <span className="stelle__name">
            {s.herkunft === 'angeordnet' && (
              <span title="Vorgabe aus der Anordnung – schreibgeschützt">🔒 </span>
            )}
            {s.bezeichnung}
          </span>
          <span className="stelle__detail">
            {s.herkunft === 'erkannt'
              ? 'Erkannte Kreuzung (nicht angeordnet)'
              : s.massnahme
                ? MASSNAHME_TEXT[s.massnahme]
                : ''}
            {s.fundstelle && ` · ${s.fundstelle}`}
            {s.station_m !== null && ` · Route-Meter ${Math.round(s.station_m)}`}
          </span>
          {s.zeiten && (
            <span className="stelle__detail">
              Besetzen ab ca. {uhrzeit(s.zeiten.besetzen)} · Zugspitze {uhrzeit(s.zeiten.ankunft)} ·
              Freigabe ca. {uhrzeit(s.zeiten.freigabe)}
              {s.sperrzeit_von &&
                ` · Sperrzeit ${uhrzeit(s.sperrzeit_von)}–${uhrzeit(s.sperrzeit_bis)}`}
            </span>
          )}
        </span>
        <StatusPill status={s.status} />
      </button>
      <ul className="befunde">
        {s.befunde
          .filter((b) => b.code !== 'OK' || s.befunde.length === 1)
          .map((b) => (
            <li key={b.schluessel}>
              <BefundZeile b={b} darf={darf} onBestaetigen={onBestaetigen} />
            </li>
          ))}
      </ul>
    </div>
  );
}

function BefundZeile({
  b,
  darf,
  onBestaetigen,
}: {
  b: Befund;
  darf: boolean;
  onBestaetigen: (b: Befund, aufheben?: boolean) => void;
}) {
  return (
    <div className={`befund befund--${b.bestaetigt ? 'bestaetigt' : b.status}`}>
      <span className="befund__text">
        <strong>{b.bestaetigt ? 'Zur Kenntnis genommen: ' : ''}</strong>
        {b.begruendung}
      </span>
      {darf && b.bestaetigbar && (
        <button
          type="button"
          className="knopf knopf--klein"
          onClick={() => onBestaetigen(b, b.bestaetigt)}
        >
          {b.bestaetigt ? 'Zurücknehmen' : 'Zur Kenntnis nehmen'}
        </button>
      )}
    </div>
  );
}

function ZugAnnahmen({
  analyse,
  parameter,
  darf,
  onSpeichern,
}: {
  analyse: Analyse;
  parameter: ZugParameter;
  darf: boolean;
  onSpeichern: (p: ZugParameter) => void;
}) {
  const [p, setP] = useState(parameter);
  useEffect(() => setP(parameter), [parameter]);
  const feld = (k: keyof ZugParameter, label: string, standard: number, step: number) => (
    <label className="feld feld--zeile">
      <span className="feld__label">{label}</span>
      <input
        type="number"
        step={step}
        value={p[k] ?? ''}
        placeholder={String(standard)}
        disabled={!darf}
        onChange={(e) => setP({ ...p, [k]: e.target.value === '' ? null : Number(e.target.value) })}
      />
    </label>
  );
  return (
    <details className="zugannahmen">
      <summary>
        Zugmodell <Annahme />
      </summary>
      <p className="gedaempft">
        Leer = Wert aus dem Regelwerk. Zuglänge ca. {Math.round(analyse.zug.laenge_m)} m, Vorlauf{' '}
        {analyse.zug.vorlauf_min} min, Nachlauf {analyse.zug.nachlauf_min} min.
      </p>
      {feld('geh_kmh', 'Gehgeschwindigkeit (km/h)', analyse.zug.geh_kmh, 0.1)}
      {feld('personen_pro_reihe', 'Personen pro Reihe', analyse.zug.personen_pro_reihe, 1)}
      {feld('reihenabstand_m', 'Reihenabstand (m)', analyse.zug.reihenabstand_m, 0.1)}
      {darf && (
        <button type="button" className="knopf" onClick={() => onSpeichern(p)}>
          Übernehmen und neu prüfen
        </button>
      )}
    </details>
  );
}

function WerkzeugKnopf(props: {
  aktiv: boolean;
  symbol: string;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      className={`werkzeug${props.aktiv ? ' werkzeug--aktiv' : ''}`}
      aria-pressed={props.aktiv}
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
    >
      <span className="werkzeug__symbol" aria-hidden="true">
        {props.symbol}
      </span>
      <span>{props.text}</span>
    </button>
  );
}

function StatusZeile({
  zustand,
  laenge,
  werkzeug,
}: {
  zustand: Zustand;
  laenge: number | null;
  werkzeug: Werkzeug;
}) {
  let text: string;
  let klasse = 'bereit';
  if (typeof zustand === 'object') {
    text = zustand.fehler;
    klasse = 'fehler';
  } else if (zustand === 'laedt') text = 'Lädt …';
  else if (zustand === 'speichert') text = 'Route wird gespeichert …';
  else if (zustand === 'prueft')
    text =
      'Abgleich läuft … Beim ersten Mal in einem Gebiet kann das wegen der Straßendaten bis zu zwei Minuten dauern.';
  else if (werkzeug === 'route')
    text = 'Route zeichnen: Punkte antippen, letzten Punkt doppelt antippen zum Abschließen.';
  else if (werkzeug === 'bearbeiten')
    text =
      'Route ändern: Linie antippen, dann Punkte ziehen; Zwischenpunkte ziehen fügt Punkte ein. Jede Änderung wird gespeichert.';
  else if (laenge !== null)
    text = `Route ${Math.round(laenge).toLocaleString('de-DE')} m · gespeichert und geprüft`;
  else text = 'Noch keine Route. Werkzeug „Route“ wählen und auf der Karte zeichnen.';
  return (
    <p className={`statuszeile statuszeile--${klasse}`} role="status" aria-live="polite">
      {text}
    </p>
  );
}

function stellePopup(s: Stelle): HTMLElement {
  // DOM statt HTML-String: Namen aus OSM bzw. Eingaben werden nie als HTML interpretiert.
  const div = document.createElement('div');
  div.className = 'popup';
  const zeile = (text: string, fett = false) => {
    const e = document.createElement(fett ? 'strong' : 'div');
    e.textContent = text;
    div.append(e);
  };
  zeile(`${s.kennung && s.herkunft !== 'erkannt' ? s.kennung + ' – ' : ''}${s.bezeichnung}`, true);
  zeile(
    `Status: ${s.status === 'rot' ? 'Rot – klären' : s.status === 'gelb' ? 'Gelb – prüfen' : s.status === 'gruen' ? 'Grün' : s.status === 'polizei' ? 'Polizei' : 'Hinweis'}`,
  );
  if (s.massnahme)
    zeile(`${MASSNAHME_TEXT[s.massnahme]}${s.fundstelle ? ` (${s.fundstelle})` : ''}`);
  s.wege.forEach((w) => zeile(`${w.name ?? 'ohne Namen'} · ${wegeklasse(w)} · ${tempo(w)}`));
  if (s.zeiten)
    zeile(
      `Besetzen ab ca. ${uhrzeit(s.zeiten.besetzen)}, Freigabe ca. ${uhrzeit(s.zeiten.freigabe)}`,
    );
  s.befunde.forEach((b) => zeile(`• ${b.begruendung}`));
  return div;
}
